import type { AnchoredRow } from '../types/anchoredRowTypes'
import {
  STRUCTURAL_RANGE_KINDS,
  STRUCTURAL_RANGE_LAYER_SCHEMA_VERSION,
  STRUCTURAL_RANGE_TAXONOMY_VERSION,
  type ChapterStructuralRangeLayer,
  type StructuralRange,
  type StructuralRangeContext,
  type StructuralRangeContextEntry,
  type StructuralRangeDiagnostic,
  type StructuralRangeKind,
} from '../types/structuralRangeTypes'

import {
  STRUCTURAL_RANGE_KIND_LABELS, isStructuralChildKindAllowed,
  requiresStructuralTaxonomyV2, structuralNarrativeIncrement,
} from './structuralRangeTaxonomy'
export { STRUCTURAL_RANGE_KIND_LABELS, isStructuralChildKindAllowed } from './structuralRangeTaxonomy'

const clean = (value: unknown): string => String(value || '').replace(/\s+/g, ' ').trim()

const generateId = (prefix: string): string => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `${prefix}:${crypto.randomUUID()}`
    : `${prefix}:${Date.now()}:${Math.random().toString(16).slice(2)}`
)

export const createChapterStructuralRangeLayer = ({
  chapterId,
  bookId,
  now = new Date().toISOString(),
}: {
  chapterId: string
  bookId?: string
  now?: string
}): ChapterStructuralRangeLayer => ({
  schemaVersion: STRUCTURAL_RANGE_LAYER_SCHEMA_VERSION,
  taxonomyVersion: STRUCTURAL_RANGE_TAXONOMY_VERSION,
  id: chapterId,
  chapterId,
  bookId,
  version: 1,
  ranges: [],
  createdAt: now,
  updatedAt: now,
})

export const buildStructuralRowOrder = (
  rows: readonly Pick<AnchoredRow, 'id' | 'position' | 'labelNumber'>[],
): Map<string, number> => new Map(
  [...rows]
    .sort((left, right) => (
      left.position - right.position
      || left.labelNumber - right.labelNumber
      || left.id.localeCompare(right.id)
    ))
    .map((row, index) => [row.id, index]),
)

const boundsFor = (
  range: Pick<StructuralRange, 'startRowId' | 'endRowId'>,
  rowOrderById: ReadonlyMap<string, number>,
): [number, number] | null => {
  const start = rowOrderById.get(range.startRowId)
  const end = rowOrderById.get(range.endRowId)
  return start === undefined || end === undefined ? null : [start, end]
}

const containsBounds = (
  outer: [number, number],
  inner: [number, number],
): boolean => outer[0] <= inner[0] && outer[1] >= inner[1]

const strictlyContainsBounds = (
  outer: [number, number],
  inner: [number, number],
): boolean => containsBounds(outer, inner) && (outer[0] < inner[0] || outer[1] > inner[1])

const rangesCross = (left: [number, number], right: [number, number]): boolean => (
  (left[0] < right[0] && right[0] <= left[1] && left[1] < right[1])
  || (right[0] < left[0] && left[0] <= right[1] && right[1] < left[1])
)

export const validateStructuralRangeLayer = ({
  layer,
  rowOrderById,
}: {
  layer: ChapterStructuralRangeLayer
  rowOrderById: ReadonlyMap<string, number>
}): StructuralRangeDiagnostic[] => {
  const diagnostics: StructuralRangeDiagnostic[] = []
  const rangeById = new Map(layer.ranges.map(range => [range.id, range]))
  const boundsById = new Map<string, [number, number]>()

  layer.ranges.forEach(range => {
    if (range.kind !== null && !isStructuralRangeKind(range.kind)) {
      diagnostics.push({ severity: 'error', code: 'invalid-kind',
        message: 'The range has an unsupported structural classification.', rangeId: range.id })
    }
    const bounds = boundsFor(range, rowOrderById)
    if (!bounds) {
      diagnostics.push({
        severity: 'error',
        code: 'missing-row',
        message: 'The range refers to a row outside the current chapter.',
        rangeId: range.id,
      })
      return
    }
    boundsById.set(range.id, bounds)
    if (bounds[0] > bounds[1]) {
      diagnostics.push({
        severity: 'error',
        code: 'reversed-range',
        message: 'The range ends before it starts.',
        rangeId: range.id,
      })
    }
    if (!range.kind || range.status === 'unclassified') {
      diagnostics.push({
        severity: 'warning',
        code: 'unclassified',
        message: 'The imported range still needs a structural classification.',
        rangeId: range.id,
      })
    }
    if (
      !range.parentRangeId
      && range.status === 'confirmed'
      && range.kind
      && !['frame', 'tale', 'frame_dialogue', 'discourse', 'monologue', 'subtale'].includes(range.kind)
    ) {
      diagnostics.push({
        severity: 'warning',
        code: 'unexpected-root',
        message: `${STRUCTURAL_RANGE_KIND_LABELS[range.kind]} is currently a root range.`,
        rangeId: range.id,
      })
    }
  })

  for (let leftIndex = 0; leftIndex < layer.ranges.length; leftIndex += 1) {
    const left = layer.ranges[leftIndex]
    const leftBounds = boundsById.get(left.id)
    if (!leftBounds || left.status === 'rejected') continue
    for (let rightIndex = leftIndex + 1; rightIndex < layer.ranges.length; rightIndex += 1) {
      const right = layer.ranges[rightIndex]
      const rightBounds = boundsById.get(right.id)
      if (!rightBounds || right.status === 'rejected') continue
      if (rangesCross(leftBounds, rightBounds)) {
        diagnostics.push({
          severity: 'error',
          code: 'crossing-ranges',
          message: 'Structural ranges may nest or remain disjoint, but their boundaries may not cross.',
          rangeId: left.id,
          relatedRangeId: right.id,
        })
      }
    }
  }

  layer.ranges.forEach(range => {
    if (!range.parentRangeId) return
    const parent = rangeById.get(range.parentRangeId)
    if (!parent) {
      diagnostics.push({
        severity: 'error',
        code: 'missing-parent',
        message: 'The selected parent range no longer exists.',
        rangeId: range.id,
        relatedRangeId: range.parentRangeId,
      })
      return
    }
    const parentBounds = boundsById.get(parent.id)
    const childBounds = boundsById.get(range.id)
    if (parentBounds && childBounds && !strictlyContainsBounds(parentBounds, childBounds)) {
      diagnostics.push({
        severity: 'error',
        code: 'parent-does-not-enclose',
        message: 'A parent range must strictly enclose its child.',
        rangeId: range.id,
        relatedRangeId: parent.id,
      })
    }
    if (parent.kind && range.kind && !isStructuralChildKindAllowed(parent.kind, range.kind)) {
      diagnostics.push({
        severity: 'error',
        code: parent.kind === 'transition' ? 'transition-has-children' : 'invalid-parent-kind',
        message: `${STRUCTURAL_RANGE_KIND_LABELS[parent.kind]} cannot contain ${(STRUCTURAL_RANGE_KIND_LABELS[range.kind] || range.kind).toLowerCase()}.`,
        rangeId: range.id,
        relatedRangeId: parent.id,
      })
    }
  })

  layer.ranges.forEach(range => {
    const visited = new Set<string>([range.id])
    let parentId = range.parentRangeId
    while (parentId) {
      if (visited.has(parentId)) {
        diagnostics.push({
          severity: 'error',
          code: 'cycle',
          message: 'The range hierarchy contains a cycle.',
          rangeId: range.id,
          relatedRangeId: parentId,
        })
        break
      }
      visited.add(parentId)
      parentId = rangeById.get(parentId)?.parentRangeId || null
    }
  })

  return diagnostics
}

export const findStructuralRangeParentOptions = ({
  layer,
  rangeId,
  kind,
  startRowId,
  endRowId,
  rowOrderById,
}: {
  layer: ChapterStructuralRangeLayer
  rangeId?: string
  kind: StructuralRangeKind | null
  startRowId: string
  endRowId: string
  rowOrderById: ReadonlyMap<string, number>
}): StructuralRange[] => {
  const childBounds = boundsFor({ startRowId, endRowId }, rowOrderById)
  if (!childBounds) return []
  return layer.ranges
    .filter(candidate => {
      if (candidate.id === rangeId || candidate.status === 'rejected' || !candidate.kind) return false
      const candidateBounds = boundsFor(candidate, rowOrderById)
      return Boolean(
        candidateBounds
        && strictlyContainsBounds(candidateBounds, childBounds)
        && (!kind || isStructuralChildKindAllowed(candidate.kind, kind))
      )
    })
    .sort((left, right) => {
      const leftBounds = boundsFor(left, rowOrderById) as [number, number]
      const rightBounds = boundsFor(right, rowOrderById) as [number, number]
      const leftLength = leftBounds[1] - leftBounds[0]
      const rightLength = rightBounds[1] - rightBounds[0]
      return leftLength - rightLength || left.title.localeCompare(right.title)
    })
}

export const findDefaultStructuralRangeParentId = (
  options: StructuralRange[],
): string | null => options[0]?.id || null

const normalizeRange = (range: StructuralRange, now: string): StructuralRange => ({
  ...range,
  title: clean(range.title),
  parentRangeId: range.parentRangeId || null,
  confidence: typeof range.confidence === 'number' && Number.isFinite(range.confidence)
    ? Math.max(0, Math.min(1, range.confidence))
    : null,
  updatedAt: now,
})

export const upsertStructuralRange = ({
  layer,
  range,
  rowOrderById,
  now = new Date().toISOString(),
}: {
  layer: ChapterStructuralRangeLayer
  range: StructuralRange
  rowOrderById: ReadonlyMap<string, number>
  now?: string
}): ChapterStructuralRangeLayer => {
  const normalized = normalizeRange(range, now)
  if (!normalized.title) throw new Error('A structural range needs a title.')
  if (normalized.status === 'confirmed' && !normalized.kind) {
    throw new Error('Choose a structural kind before confirming the range.')
  }
  const ranges = layer.ranges.some(candidate => candidate.id === normalized.id)
    ? layer.ranges.map(candidate => candidate.id === normalized.id ? normalized : candidate)
    : [...layer.ranges, normalized]
  const updated: ChapterStructuralRangeLayer = {
    ...layer,
    taxonomyVersion: ranges.some(candidate => requiresStructuralTaxonomyV2(candidate.kind))
      ? STRUCTURAL_RANGE_TAXONOMY_VERSION : layer.taxonomyVersion,
    version: layer.version + 1,
    ranges,
    updatedAt: now,
  }
  const errors = validateStructuralRangeLayer({ layer: updated, rowOrderById })
    .filter(diagnostic => diagnostic.severity === 'error')
  if (errors.length) throw new Error(errors[0].message)
  return updated
}

export const createStructuralRange = ({
  title,
  kind,
  startRowId,
  endRowId,
  parentRangeId = null,
  status = 'confirmed',
  origin = 'human',
  now = new Date().toISOString(),
}: Pick<StructuralRange, 'title' | 'kind' | 'startRowId' | 'endRowId'>
  & Partial<Pick<StructuralRange, 'parentRangeId' | 'status' | 'origin'>>
  & { now?: string }): StructuralRange => ({
  id: generateId('structural-range'),
  title: clean(title),
  kind,
  startRowId,
  endRowId,
  parentRangeId,
  status,
  origin,
  confidence: null,
  createdAt: now,
  updatedAt: now,
})

export const removeStructuralRange = ({
  layer,
  rangeId,
  rowOrderById,
  now = new Date().toISOString(),
}: {
  layer: ChapterStructuralRangeLayer
  rangeId: string
  rowOrderById: ReadonlyMap<string, number>
  now?: string
}): ChapterStructuralRangeLayer => {
  const removed = layer.ranges.find(range => range.id === rangeId)
  if (!removed) return layer
  const ranges = layer.ranges
    .filter(range => range.id !== rangeId)
    .map(range => range.parentRangeId === rangeId
      ? { ...range, parentRangeId: removed.parentRangeId, updatedAt: now }
      : range)
  const updated = { ...layer, ranges, version: layer.version + 1, updatedAt: now }
  const errors = validateStructuralRangeLayer({ layer: updated, rowOrderById })
    .filter(diagnostic => diagnostic.severity === 'error')
  if (errors.length) throw new Error(errors[0].message)
  return updated
}

export const buildStructuralRangeContext = ({
  layer,
  rowId,
  rowOrderById,
}: {
  layer: ChapterStructuralRangeLayer
  rowId: string
  rowOrderById: ReadonlyMap<string, number>
}): StructuralRangeContext => {
  const targetIndex = rowOrderById.get(rowId)
  if (targetIndex === undefined) {
    return {
      rowId,
      activeRangePath: [],
      immediateParent: null,
      children: [],
      previousSibling: null,
      nextSibling: null,
    }
  }
  const confirmed = layer.ranges.filter(range => range.status === 'confirmed' && range.kind)
  const rangeById = new Map(confirmed.map(range => [range.id, range]))
  const depthById = new Map<string, number>()
  const levelById = new Map<string, number>()

  const resolveMetrics = (range: StructuralRange): [number, number] => {
    if (depthById.has(range.id)) {
      return [depthById.get(range.id) as number, levelById.get(range.id) as number]
    }
    const parent = range.parentRangeId ? rangeById.get(range.parentRangeId) : null
    const [parentDepth, parentLevel] = parent ? resolveMetrics(parent) : [-1, 0]
    const depth = parentDepth + 1
    const level = parent
      ? parentLevel + structuralNarrativeIncrement(range.kind)
      : 0
    depthById.set(range.id, depth)
    levelById.set(range.id, level)
    return [depth, level]
  }

  const toEntry = (range: StructuralRange): StructuralRangeContextEntry => {
    const [depth, narrativeLevel] = resolveMetrics(range)
    return {
      id: range.id,
      title: range.title,
      kind: range.kind as StructuralRangeKind,
      startRowId: range.startRowId,
      endRowId: range.endRowId,
      parentRangeId: range.parentRangeId,
      depth,
      narrativeLevel,
    }
  }

  const activeRangePath = confirmed
    .filter(range => {
      const bounds = boundsFor(range, rowOrderById)
      return Boolean(bounds && bounds[0] <= targetIndex && targetIndex <= bounds[1])
    })
    .map(toEntry)
    .sort((left, right) => left.depth - right.depth)
  const active = activeRangePath[activeRangePath.length - 1] || null
  const immediateParent = active?.parentRangeId
    ? activeRangePath.find(entry => entry.id === active.parentRangeId) || null
    : null
  const siblingRanges = active
    ? confirmed
      .filter(range => range.parentRangeId === active.parentRangeId)
      .sort((left, right) => (
        (rowOrderById.get(left.startRowId) ?? Number.MAX_SAFE_INTEGER)
        - (rowOrderById.get(right.startRowId) ?? Number.MAX_SAFE_INTEGER)
      ))
    : []
  const siblingIndex = active ? siblingRanges.findIndex(range => range.id === active.id) : -1
  const children = active
    ? confirmed
      .filter(range => range.parentRangeId === active.id)
      .map(toEntry)
      .sort((left, right) => (
        (rowOrderById.get(left.startRowId) ?? Number.MAX_SAFE_INTEGER)
        - (rowOrderById.get(right.startRowId) ?? Number.MAX_SAFE_INTEGER)
      ))
    : []

  return {
    rowId,
    activeRangePath,
    immediateParent,
    children,
    previousSibling: siblingIndex > 0 ? toEntry(siblingRanges[siblingIndex - 1]) : null,
    nextSibling: siblingIndex >= 0 && siblingIndex < siblingRanges.length - 1
      ? toEntry(siblingRanges[siblingIndex + 1])
      : null,
  }
}

export const isStructuralRangeKind = (value: unknown): value is StructuralRangeKind => (
  STRUCTURAL_RANGE_KINDS.includes(value as StructuralRangeKind)
)
